import { useState, useRef, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, BarChart3, Bookmark, BookmarkCheck, X, Trash2, Pin } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Input } from "@/components/ui/input";
import { StatsStrip } from "@/components/StatsStrip";
import { TaxSummary } from "@/components/TaxSummary";
import { PeriodFilter } from "@/components/PeriodFilter";
import { periodBounds, tourInBounds, periodLabelFor } from "@/lib/period";
import { api } from "@/lib/api";

const PALETTE = ["#9B2226", "#E0575B", "#c2686b", "#7C83FF", "#34d399", "#a1a1aa", "#71717a"];
const PIN_KEY = "insightsPinned";
const TURNS_KEY = "insightsTurns";

const loadTurns = () => {
  try { const t = JSON.parse(localStorage.getItem(TURNS_KEY) || "[]"); return Array.isArray(t) ? t : []; }
  catch { return []; }
};

const EXAMPLES = [
  "Who are my top 5 tippers?",
  "List my repeat guests this year",
  "Which guests did I tour in July 2025?",
  "PEP vs Premium average tips",
];

const axisTick = { fill: "#71717a", fontSize: 11 };

const loadPinned = () => {
  try { return JSON.parse(localStorage.getItem(PIN_KEY) || "[]"); } catch { return []; }
};

const AiTable = ({ table }) => {
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const cols = Array.isArray(table?.columns) ? table.columns : [];
  const baseRows = Array.isArray(table?.rows) ? table.rows : [];
  const rows = useMemo(() => {
    if (!sort.key) return baseRows;
    return [...baseRows].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av === null || av === undefined || av === "") return 1;
      if (bv === null || bv === undefined || bv === "") return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * sort.dir;
      return String(av).localeCompare(String(bv)) * sort.dir;
    });
  }, [baseRows, sort]);

  if (!cols.length || !baseRows.length) return null;
  const fmt = (col, v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (col.money && typeof v === "number") return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return String(v);
  };
  const toggle = (k) => setSort((s) => (s.key === k ? { key: k, dir: -s.dir } : { key: k, dir: 1 }));

  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900" data-testid="insights-table">
      {table.title && <h3 className="px-4 pt-3 font-display text-sm font-700 tracking-tight text-zinc-200">{table.title}</h3>}
      <div className="mt-2 max-h-96 overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-zinc-950">
            <tr className="text-left text-xs uppercase tracking-wide text-zinc-400">
              {cols.map((c) => (
                <th key={c.key} className="cursor-pointer select-none whitespace-nowrap border-b border-zinc-800 px-4 py-2 font-600 hover:text-zinc-200" onClick={() => toggle(c.key)} data-testid={`insights-table-col-${c.key}`}>
                  {c.name || c.key}{sort.key === c.key ? (sort.dir === 1 ? " ↑" : " ↓") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-zinc-800/50 last:border-0 hover:bg-white/[0.02]" data-testid="insights-table-row">
                {cols.map((c) => (
                  <td key={c.key} className="whitespace-nowrap px-4 py-2 text-zinc-200">{fmt(c, r[c.key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AiChart = ({ chart }) => {
  if (!chart || !Array.isArray(chart.data) || chart.data.length === 0) return null;
  const series = chart.series && chart.series.length ? chart.series : [{ key: "value", name: chart.y_label || "Value" }];

  return (
    <div className="mt-3 rounded-xl border border-zinc-800 bg-zinc-900 p-4" data-testid="insights-chart">
      {chart.title && <h3 className="mb-3 font-display text-sm font-700 tracking-tight text-zinc-200">{chart.title}</h3>}
      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {chart.type === "pie" ? (
            <PieChart>
              <Pie data={chart.data} dataKey={series[0].key} nameKey="label" cx="50%" cy="50%" outerRadius={100} label={(e) => e.label}>
                {chart.data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #27272a", borderRadius: 10, fontSize: 12 }} />
            </PieChart>
          ) : chart.type === "line" ? (
            <LineChart data={chart.data} margin={{ top: 6, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,148,160,0.15)" vertical={false} />
              <XAxis dataKey="label" tick={axisTick} tickLine={false} axisLine={false} minTickGap={16} />
              <YAxis tick={axisTick} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #27272a", borderRadius: 10, fontSize: 12 }} />
              {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
              {series.map((s, i) => (
                <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          ) : (
            <BarChart data={chart.data} margin={{ top: 6, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,148,160,0.15)" vertical={false} />
              <XAxis dataKey="label" tick={axisTick} tickLine={false} axisLine={false} minTickGap={8} />
              <YAxis tick={axisTick} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #27272a", borderRadius: 10, fontSize: 12 }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
              {series.map((s, i) => (
                <Bar key={s.key} dataKey={s.key} name={s.name} fill={PALETTE[i % PALETTE.length]} radius={[4, 4, 0, 0]} maxBarSize={48} />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const InsightsView = ({ tours }) => {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [turns, setTurns] = useState(loadTurns); // { q, a, chart, table }
  const [pending, setPending] = useState(null); // question currently being asked
  const [pinned, setPinned] = useState(loadPinned);
  const [period, setPeriod] = useState(() => localStorage.getItem("insightsPeriod") || "all");
  const [pFrom, setPFrom] = useState(() => localStorage.getItem("insightsFrom") || "");
  const [pTo, setPTo] = useState(() => localStorage.getItem("insightsTo") || "");
  useEffect(() => {
    localStorage.setItem("insightsPeriod", period);
    localStorage.setItem("insightsFrom", pFrom);
    localStorage.setItem("insightsTo", pTo);
  }, [period, pFrom, pTo]);
  const scopedTours = useMemo(() => {
    const b = periodBounds(period, pFrom, pTo);
    return (tours || []).filter((t) => tourInBounds(t, b));
  }, [tours, period, pFrom, pTo]);
  const threadEndRef = useRef(null);
  const lastQRef = useRef("");

  // Remember the last conversation so returning to Insights shows the same answers.
  useEffect(() => {
    try { localStorage.setItem(TURNS_KEY, JSON.stringify(turns.slice(-10))); } catch { /* ignore quota */ }
  }, [turns]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [turns, loading]);

  const savePinned = (arr) => { setPinned(arr); localStorage.setItem(PIN_KEY, JSON.stringify(arr)); };
  const isPinned = (q) => pinned.includes(q);
  const togglePin = (q) => {
    if (isPinned(q)) { savePinned(pinned.filter((x) => x !== q)); toast.info("Removed from pinned"); }
    else { savePinned([q, ...pinned].slice(0, 12)); toast.success("Pinned to the top"); }
  };

  const ask = async (q) => {
    const query = (q ?? question).trim();
    if (!query || loading) return;
    lastQRef.current = query;
    setQuestion("");
    setError(false);
    setPending(query);
    setLoading(true);
    const history = turns.map((t) => ({ question: t.q, answer: t.a }));
    try {
      const data = await api.askInsights(query, history);
      setTurns((prev) => [...prev, { q: query, a: data.answer, chart: data.chart, table: data.table }]);
    } catch {
      setError(true);
      toast.error("Couldn't analyze that — try rephrasing.");
    } finally {
      setLoading(false);
      setPending(null);
    }
  };

  const reset = () => { setTurns([]); setError(false); setPending(null); };

  return (
    <div className="space-y-6" data-testid="insights-view">
      <div>
        <h2 className="mb-1 flex items-center gap-2 font-display text-xl font-800 tracking-tight text-zinc-50">
          <BarChart3 className="h-5 w-5 text-[#E0575B]" /> Insights
        </h2>
        <p className="text-sm text-zinc-400">Your numbers at a glance — plus ask anything about your tours.</p>
      </div>

      {/* Pinned questions */}
      {pinned.length > 0 && (
        <div data-testid="insights-pinned">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-600 uppercase tracking-wide text-zinc-400">
            <Pin className="h-3.5 w-3.5 text-[#E0575B]" /> Pinned
          </div>
          <div className="flex flex-wrap gap-2">
            {pinned.map((q) => (
              <div key={q} className="group flex items-center gap-1.5 rounded-full border border-[#9B2226]/30 bg-[#9B2226]/10 py-1 pl-3 pr-1.5 text-sm text-[#E0575B]" data-testid="insights-pinned-item">
                <button onClick={() => ask(q)} disabled={loading} className="max-w-[220px] truncate text-left hover:text-[#E0575B] disabled:opacity-50" title={q}>{q}</button>
                <button onClick={() => togglePin(q)} className="rounded-full p-1 text-[#E0575B]/60 hover:bg-[#7A1A1E]/20 hover:text-[#E0575B]" data-testid="insights-pin-remove" title="Unpin">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2" data-testid="insights-period-row">
        <PeriodFilter value={period} onChange={setPeriod} from={pFrom} to={pTo} onFrom={setPFrom} onTo={setPTo} testid="insights-period" />
        <span className="text-xs text-zinc-500" data-testid="insights-period-label">Stats: {periodLabelFor(period)} · {scopedTours.length} tour{scopedTours.length === 1 ? "" : "s"}</span>
      </div>

      <StatsStrip tours={scopedTours} governed />

      <TaxSummary tours={tours} />

      {/* Ask your data */}
      <div className="rounded-2xl lg-glass border border-[#9B2226]/40 p-5" data-testid="ask-data-card">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#9B2226]/20 text-[#E0575B]">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <h3 className="font-display text-base font-700 tracking-tight text-zinc-50">Ask your data</h3>
              <p className="text-xs text-zinc-400">Ask anything — names, numbers, tables or charts, in plain English.</p>
            </div>
          </div>
          {turns.length > 0 && (
            <button onClick={reset} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-300 hover:text-zinc-100" data-testid="insights-clear-btn">
              <Trash2 className="h-3.5 w-3.5" /> New chat
            </button>
          )}
        </div>

        {/* Conversation thread */}
        {(turns.length > 0 || pending) && (
          <div className="mb-4 space-y-4" data-testid="insights-thread">
            {turns.map((t, i) => (
              <div key={i} className="space-y-2">
                <div className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[#9B2226] px-3.5 py-2 text-sm text-white shadow-md" data-testid="insights-turn-question">{t.q}</div>
                </div>
                <div className="lg-glass rounded-2xl rounded-bl-sm p-4">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm leading-relaxed text-zinc-100" data-testid="insights-answer">{t.a}</p>
                    <button onClick={() => togglePin(t.q)} className="shrink-0 rounded-md p-2 text-zinc-400 transition-colors hover:bg-white/5 hover:text-[#E0575B]" data-testid="insights-pin-btn" title={isPinned(t.q) ? "Unpin question" : "Pin question"}>
                      {isPinned(t.q) ? <BookmarkCheck className="h-4 w-4 text-[#E0575B]" /> : <Bookmark className="h-4 w-4" />}
                    </button>
                  </div>
                  <AiTable table={t.table} />
                  <AiChart chart={t.chart} />
                </div>
              </div>
            ))}
            {pending && (
              <div className="space-y-2">
                <div className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[#9B2226] px-3.5 py-2 text-sm text-white shadow-md">{pending}</div>
                </div>
                <div className="lg-glass flex items-center gap-2 rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-zinc-300" data-testid="insights-loading">
                  <Loader2 className="h-4 w-4 animate-spin text-[#E0575B]" /> Crunching your numbers…
                </div>
              </div>
            )}
            <div ref={threadEndRef} />
          </div>
        )}

        {error && !loading && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="insights-error">
            <span>Something went wrong analyzing that. Please try again.</span>
            <button onClick={() => ask(lastQRef.current)} className="rounded-md border border-red-500/40 px-2.5 py-1 text-xs font-600 text-red-200 hover:bg-red-500/20" data-testid="insights-retry-btn">Retry</button>
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex items-center gap-2">
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={turns.length ? "Ask a follow-up…" : "e.g. What's my return rate this year?"}
            className="border-zinc-700 bg-zinc-900 focus-visible:ring-[#E0575B]/70"
            data-testid="insights-question-input"
          />
          <button
            type="submit"
            disabled={loading || !question.trim()}
            className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-md bg-[#9B2226] px-4 text-sm font-600 text-white transition-colors hover:bg-[#7A1A1E] disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="insights-ask-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Ask
          </button>
        </form>

        {turns.length === 0 && !pending && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="insights-examples">
            {EXAMPLES.map((ex) => (
              <button
                key={ex}
                onClick={() => ask(ex)}
                disabled={loading}
                className="rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1 text-xs text-zinc-300 transition-colors hover:border-[#9B2226]/50 hover:text-[#E0575B] disabled:opacity-50"
                data-testid="insights-example-chip"
              >
                {ex}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
